/**
 * R9 Vorsteuer (§ 15 Abs. 1 Nr. 1, Abs. 2, Abs. 4 UStG): nur Regelbesteuerung, nur Ausgaben mit MwSt
 * und Satz > 0, keine Darlehenskonten; Gutschriften negativ. Monat = der spätere Kalendermonat von
 * `invoiceDate ?? date` und `serviceEndDate ?? invoiceDate ?? date` (gleicher Monat → Rechnungsdatum).
 *
 * Aufteilung (§ 15 Abs. 4): SET_INPUT_TAX_SHARE-Adjustment > direkte Zurechnung über `unitsID`
 * (homogen steuerpflichtig → 1, homogen steuerfrei → 0) > Schlüssel des vatPropertyProfiles
 * (VOLL / KEIN / MANUELL / AUTO_FLAECHE = steuerpflichtige Fläche / Gesamtfläche des Monats).
 * Leerstand nach Verwendungsabsicht; fehlende Fläche bei AUTO_FLAECHE blockt; share 0 bleibt sichtbar.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
import {createIssue} from '../issues';
import {buildLine, buildSource, expenseDeepLink, unitsDeepLink} from '../line';
import {monthEnd, monthKey, monthStart, parseMonthKey} from '../month';
import {isTaxableCategory, resolveVatCategory} from '../resolve-vat-category';
import {round2, toRatePercent} from '../rounding';
import {EMPTY_RULE_OUTPUT, ExpenseLite, InputTaxByProperty, InputTaxKey, PeriodLite, RuleContext, RuleOutput, UnitLite, VatIssue} from '../types';
import {unitAreaForMonth} from '../unit-area';
import {issueIDsOf, linkIssues} from './exclusions';

export const DEFAULT_INPUT_TAX_KEY: InputTaxKey = {mode: 'AUTO_FLAECHE', manualShare: null, note: null};

export const resolveInputTaxKey = (ctx: RuleContext, propertyID: string, year: number): InputTaxKey => {
    const profile = ctx.propertyProfiles[propertyID];
    return profile?.yearOverrides?.[String(year)] ?? profile?.inputTaxKey ?? DEFAULT_INPUT_TAX_KEY;
};

/** Jahr eines monthKeys; bei ungültigem Schlüssel das Jahr des Voranmeldungszeitraums. */
const yearOfKey = (ctx: RuleContext, key: string): number => parseMonthKey(key)?.year ?? ctx.range.year;

const coversMonth = (period: PeriodLite, key: string): boolean => {
    const start = monthStart(key).getTime();
    const end = monthEnd(key).getTime();
    const from = period.startDate ? period.startDate.getTime() : Number.NEGATIVE_INFINITY;
    const to = period.endDate ? period.endDate.getTime() : Number.POSITIVE_INFINITY;
    return from <= end && to >= start;
};

export interface UnitUsage {
    taxable: boolean;
    vacant: boolean;
    intentMissing: boolean;
}

/** Nutzung einer Einheit im Monat: deckende Vermietung(en) → Kategorie; sonst Leerstand nach Verwendungsabsicht. */
export const unitUsageInMonth = (ctx: RuleContext, unit: UnitLite, key: string): UnitUsage => {
    const covering = (ctx.periodsByUnit.get(unit.id) ?? []).filter(p => coversMonth(p, key));
    if (covering.length > 0) {
        return {taxable: covering.some(p => isTaxableCategory(resolveVatCategory(p).category)), vacant: false, intentMissing: false};
    }
    const intent = ctx.propertyProfiles[unit.propertyID]?.vacancyIntent?.[unit.id];
    if (intent) {
        return {taxable: intent === 'STEUERPFLICHTIG', vacant: true, intentMissing: false};
    }
    return {taxable: false, vacant: true, intentMissing: true};
};

export interface InputTaxShareResult {
    share: number;
    mode: string;
    taxableArea: number;
    totalArea: number;
    issues: VatIssue[];
}

const clamp01 = (value: number | null): number => (value === null || !Number.isFinite(value) ? 0 : Math.min(1, Math.max(0, value)));

const isUsableArea = (area: number | null): area is number => area !== null && Number.isFinite(area) && area > 0;

/** Anzeigename in Hinweistexten (Issue-Keys bleiben `unit.id`). */
const unitDisplayName = (unit: UnitLite): string => unit.label ?? unit.id;

const vacancyIntentIssue = (unit: UnitLite): VatIssue => createIssue('VACANCY_INTENT', unit.id,
    `Die Einheit ${unitDisplayName(unit)} steht leer und hat keine Verwendungsabsicht. Ohne Angabe zählt sie als steuerfrei genutzt (kein Vorsteuerabzug).`,
    {deepLink: unitsDeepLink(unit.propertyID)});

interface AreaSummary {
    taxableArea: number;
    totalArea: number;
    issues: VatIssue[];
}

/** Flächen der Einheiten im Monat; `strict` (AUTO_FLAECHE) meldet fehlende Flächen als BLOCKER. */
const summarizeAreas = (ctx: RuleContext, units: UnitLite[], key: string, strict: boolean): AreaSummary => {
    const issues: VatIssue[] = [];
    let taxableArea = 0;
    let totalArea = 0;
    for (const unit of units) {
        const usage = unitUsageInMonth(ctx, unit, key);
        if (usage.intentMissing) {
            issues.push(vacancyIntentIssue(unit));
        }
        const area = unitAreaForMonth(unit, key);
        if (!isUsableArea(area)) {
            if (strict) {
                issues.push(createIssue('AREA_MISSING', unit.id,
                    `Für die Einheit ${unitDisplayName(unit)} ist keine Fläche hinterlegt – der Flächenschlüssel kann nicht berechnet werden.`,
                    {deepLink: unitsDeepLink(unit.propertyID)}));
            }
            continue;
        }
        totalArea += area;
        if (usage.taxable) {
            taxableArea += area;
        }
    }
    return {taxableArea: round2(taxableArea), totalArea: round2(totalArea), issues};
};

const keyedShare = (ctx: RuleContext, units: UnitLite[], key: string, inputTaxKey: InputTaxKey): InputTaxShareResult => {
    const areas = summarizeAreas(ctx, units, key, inputTaxKey.mode === 'AUTO_FLAECHE');
    switch (inputTaxKey.mode) {
        case 'VOLL':
            return {share: 1, mode: 'VOLL', taxableArea: areas.taxableArea, totalArea: areas.totalArea, issues: []};
        case 'KEIN':
            return {share: 0, mode: 'KEIN', taxableArea: areas.taxableArea, totalArea: areas.totalArea, issues: []};
        case 'MANUELL':
            return {share: clamp01(inputTaxKey.manualShare), mode: 'MANUELL', taxableArea: areas.taxableArea, totalArea: areas.totalArea, issues: []};
        default:
            return {
                share: areas.totalArea > 0 ? areas.taxableArea / areas.totalArea : 0,
                mode: 'AUTO_FLAECHE',
                taxableArea: areas.taxableArea,
                totalArea: areas.totalArea,
                issues: areas.issues,
            };
    }
};

/** Vorsteuer-Anteil einer Ausgabe im Monat: Adjustment > direkte Zurechnung (unitsID) > Schlüssel. */
export const resolveInputTaxShare = (ctx: RuleContext, expense: ExpenseLite, key: string): InputTaxShareResult => {
    const adjustment = ctx.adjustments.find(a => a.targetType === 'EXPENSE' && a.targetID === expense.id && a.action === 'SET_INPUT_TAX_SHARE');
    if (adjustment && typeof adjustment.value === 'number') {
        return {share: clamp01(adjustment.value), mode: 'ADJUSTMENT', taxableArea: 0, totalArea: 0, issues: []};
    }

    const inputTaxKey = resolveInputTaxKey(ctx, expense.propertyID, yearOfKey(ctx, key));
    const allUnits = ctx.unitsByProperty.get(expense.propertyID) ?? [];
    const selected = !expense.hasAllUnitsSelected && expense.unitsID.length > 0
        ? allUnits.filter(u => expense.unitsID.includes(u.id))
        : [];

    if (selected.length > 0) {
        const usages = selected.map(u => unitUsageInMonth(ctx, u, key));
        // nicht strikt: bei direkter Zurechnung blockt eine fehlende Fläche nicht, nur VACANCY_INTENT bleibt
        const areas = summarizeAreas(ctx, selected, key, false);
        if (usages.every(u => u.taxable)) {
            return {share: 1, mode: 'DIREKT', taxableArea: areas.totalArea, totalArea: areas.totalArea, issues: areas.issues};
        }
        if (usages.every(u => !u.taxable)) {
            return {share: 0, mode: 'DIREKT', taxableArea: 0, totalArea: areas.totalArea, issues: areas.issues};
        }
        return keyedShare(ctx, selected, key, inputTaxKey);
    }

    return keyedShare(ctx, allUnits, key, inputTaxKey);
};

/**
 * Vorsteuermonat: der spätere Kalendermonat von `invoiceDate ?? date` und `serviceEndDate ?? invoiceDate ?? date`.
 * Liegen beide im selben Monat, zählt das Rechnungsdatum (bzw. das Buchungsdatum als Ersatz).
 */
export const resolveVorsteuerMonth = (expense: ExpenseLite): { monthKey: string; monthSource: 'INVOICE_DATE' | 'SERVICE_END' | 'DATE' } | null => {
    const invoiceBase = expense.invoiceDate ?? expense.date;
    const serviceBase = expense.serviceEndDate ?? invoiceBase;
    if (!invoiceBase && !serviceBase) {
        return null;
    }
    if (!invoiceBase) {
        return {monthKey: monthKey(serviceBase), monthSource: 'SERVICE_END'};
    }
    const invoiceKey = monthKey(invoiceBase);
    const serviceKey = serviceBase ? monthKey(serviceBase) : invoiceKey;
    if (serviceKey > invoiceKey) {
        return {monthKey: serviceKey, monthSource: 'SERVICE_END'};
    }
    return {monthKey: invoiceKey, monthSource: expense.invoiceDate ? 'INVOICE_DATE' : 'DATE'};
};

const percentDE = (share: number): string => `${String(round2(share * 100)).replace('.', ',')} %`;

const modeLabel = (mode: string, share: number): string => {
    switch (mode) {
        case 'DIREKT':
            return `direkte Zurechnung ${percentDE(share)}`;
        case 'ADJUSTMENT':
            return `manuell korrigiert ${percentDE(share)}`;
        case 'MANUELL':
            return `manueller Schlüssel ${percentDE(share)}`;
        case 'VOLL':
            return 'voll abziehbar';
        case 'KEIN':
            return 'kein Abzug';
        default:
            return `Flächenschlüssel (objektbezogen) ${percentDE(share)}`;
    }
};

const isFiniteNumber = (value: number | null): boolean => value !== null && Number.isFinite(Number(value));

export const buildVorsteuerLines = (ctx: RuleContext, expenses: ExpenseLite[]): RuleOutput => {
    const out = EMPTY_RULE_OUTPUT();

    for (const expense of expenses) {
        const deepLink = expenseDeepLink(expense.id);
        const describe = `Ausgabe „${expense.description ?? expense.id}“`;

        const month = resolveVorsteuerMonth(expense);
        if (!month) {
            out.issues.push(createIssue('SOURCE_DATA_INVALID', expense.id, `${describe} hat weder Datum, Rechnungsdatum noch Leistungsende.`, {deepLink}));
            continue;
        }
        if (!isFiniteNumber(expense.amount)) {
            out.issues.push(createIssue('SOURCE_DATA_INVALID', expense.id, `${describe} hat keinen Betrag.`, {deepLink}));
            continue;
        }
        if (ctx.profileAt(monthStart(month.monthKey)).status !== 'REGELBESTEUERUNG' || expense.isLoanAccount) {
            continue;
        }

        const issues: VatIssue[] = [];
        const includesVAT = expense.includesVAT ?? true;
        const rate = toRatePercent(expense.vatRate ?? 19);
        if (expense.includesVAT === null || expense.vatRate === null) {
            const assumed = [
                expense.includesVAT === null ? 'MwSt-Kennzeichen fehlt (angenommen: enthält MwSt)' : null,
                expense.vatRate === null ? 'Steuersatz fehlt (angenommen: 19 %)' : null,
            ].filter(Boolean).join(', ');
            issues.push(createIssue('EXPENSE_DEFAULT_VAT', expense.id, `${describe}: ${assumed}. Bitte in der Ausgabe prüfen.`, {deepLink}));
        }
        if (!includesVAT) {
            out.issues.push(...issues);
            continue;
        }
        if (rate === null) {
            issues.push(createIssue('EXPENSE_DEFAULT_VAT', expense.id, `${describe} trägt den Steuersatz ${expense.vatRate} %, der nicht unterstützt wird. Kein Vorsteuerabzug berechnet.`, {deepLink}));
            out.issues.push(...issues);
            continue;
        }
        if (rate === 0) {
            out.issues.push(...issues);
            continue;
        }
        if (!expense.invoiceDate) {
            issues.push(createIssue('EXPENSE_NO_INVOICE_DATE', expense.id, `${describe} hat kein Rechnungsdatum; der Vorsteuermonat wurde aus Buchungsdatum bzw. Leistungsende bestimmt.`, {deepLink}));
        }

        // defensiv: Gutschriften (CREDIT) und negativ gespeicherte Beträge mindern die Vorsteuer
        // Nahaus speichert Ausgabenbeträge negativ (Belegablage/EüR rechnen mit Math.abs) – das Vorzeichen bestimmt allein der Typ.
        const sign = expense.type === 'CREDIT' ? -1 : 1;
        const absAmount = Math.abs(Number(expense.amount));
        const vatFull = isFiniteNumber(expense.vatAmount)
            ? Math.abs(Number(expense.vatAmount))
            : round2(absAmount - round2(absAmount / (1 + rate / 100)));

        const shareResult = resolveInputTaxShare(ctx, expense, month.monthKey);
        issues.push(...shareResult.issues);

        const source = buildSource('EXPENSE', {id: expense.id, propertyID: expense.propertyID, unitID: expense.unitsID.length === 1 ? expense.unitsID[0] : null, periodID: null});
        const line = buildLine({
            rule: 'VORSTEUER', source, monthKey: month.monthKey, monthSource: month.monthSource,
            category: null, rate, gross: sign * absAmount, tax: sign * vatFull, share: shareResult.share,
            label: `${expense.description ?? 'Ausgabe'} – Vorsteuer ${rate} %, ${modeLabel(shareResult.mode, shareResult.share)}`,
            issueIDs: issueIDsOf(issues),
        });
        out.lines.push(line);
        out.issues.push(...linkIssues(issues, line.id));
    }

    return out;
};

/** Zeile des Flächen-Nachweises (Objekt-Abrechnung, Spec 2.2/3.2): eine Einheit im Monat. */
export interface UnitAreaRow {
    unitID: string;
    label: string;                 // unit.label ?? unit.id
    categoryType: string | null;
    tenantsName: string | null;    // erste deckende Vermietung im Monat
    area: number | null;           // Fläche laut Zeitleiste (unitAreaForMonth)
    taxable: boolean;
    vacant: boolean;
    intentMissing: boolean;
}

/** Flächenschlüssel eines Objekts im Monat ohne Issues (Nachweis-Darstellung). */
export interface AreaShareForMonth {
    share: number;
    mode: string;
    taxableArea: number;
    totalArea: number;
}

const compareLabelDE = (a: string, b: string): number => a.localeCompare(b, 'de', {numeric: true, sensitivity: 'base'});

/**
 * Flächen-Nachweis: alle Einheiten der Immobilie im Monat mit Nutzung, Fläche laut Zeitleiste und Mieter der
 * ersten deckenden Vermietung; sortiert nach Label (de, numerisch: W2 vor W10).
 */
export const summarizeUnitAreasForMonth = (ctx: RuleContext, propertyID: string, key: string): UnitAreaRow[] =>
    (ctx.unitsByProperty.get(propertyID) ?? [])
        .map((unit): UnitAreaRow => {
            const usage = unitUsageInMonth(ctx, unit, key);
            const covering = (ctx.periodsByUnit.get(unit.id) ?? []).find(p => coversMonth(p, key));
            return {
                unitID: unit.id,
                label: unitDisplayName(unit),
                categoryType: unit.categoryType ?? null,
                tenantsName: covering?.tenantsName ?? null,
                area: unitAreaForMonth(unit, key),
                taxable: usage.taxable,
                vacant: usage.vacant,
                intentMissing: usage.intentMissing,
            };
        })
        .sort((a, b) => compareLabelDE(a.label, b.label) || compareLabelDE(a.unitID, b.unitID));

/** Flächenschlüssel der Immobilie im Monat (Schlüssel laut Objektprofil inkl. Jahres-Override), ohne Issues. */
export const areaShareForMonth = (ctx: RuleContext, propertyID: string, key: string): AreaShareForMonth => {
    const inputTaxKey = resolveInputTaxKey(ctx, propertyID, yearOfKey(ctx, key));
    const {share, mode, taxableArea, totalArea} = keyedShare(ctx, ctx.unitsByProperty.get(propertyID) ?? [], key, inputTaxKey);
    return {share, mode, taxableArea, totalArea};
};

/** Vorsteuer-Schlüssel je Immobilie für den Monat (Ergebnis-Block `inputTaxByProperty`). */
export const summarizeInputTaxByProperty = (ctx: RuleContext, propertyIDs: string[], key: string): InputTaxByProperty[] =>
    Array.from(new Set(propertyIDs)).sort().map(propertyID => {
        const inputTaxKey = resolveInputTaxKey(ctx, propertyID, yearOfKey(ctx, key));
        const result = keyedShare(ctx, ctx.unitsByProperty.get(propertyID) ?? [], key, inputTaxKey);
        return {
            propertyID,
            mode: inputTaxKey.mode,
            share: Math.round(result.share * 1e6) / 1e6,
            taxableArea: result.taxableArea,
            totalArea: result.totalArea,
        };
    });
